import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';
import { LocationAutocompleteInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './LocationFilter.module.css';

const identity = v => v;

const getInitialFormValues = (address, bounds, origin) => {
  if (!address) {
    return {};
  }

  const selectedPlace = {
    address,
    ...(bounds ? { bounds } : {}),
    ...(origin ? { origin } : {}),
  };

  return {
    location: {
      search: address,
      predictions: [],
      selectedPlace,
    },
  };
};

const getLocationDataFromSubmit = values => {
  if (values === null) {
    return null;
  }

  const { location } = values || {};
  const selectedPlace = location?.selectedPlace;

  if (!selectedPlace) {
    return undefined;
  }

  return {
    location: {
      search: location?.search || '',
      selectedPlace,
    },
  };
};

const LocationField = props => {
  const { id, intl, showAsPopup } = props;
  const locationPlaceholder = intl.formatMessage({ id: 'PageBuilder.SearchCTA.locationPlaceholder' });
  const ariaLabel = intl.formatMessage({ id: 'LocationAutocompleteInput.screenreader.search' });

  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        return (
          <LocationAutocompleteInput
            id={`${id}-${showAsPopup ? 'popup' : 'plain'}-location-search`}
            className={css.locationInputRoot}
            iconClassName={css.locationInputIcon}
            inputClassName={css.locationInput}
            predictionsClassName={css.predictions}
            predictionsAttributionClassName={css.predictionsAttribution}
            placeholder={locationPlaceholder}
            closeOnBlur={showAsPopup}
            input={input}
            meta={meta}
            ariaLabel={ariaLabel}
          />
        );
      }}
    />
  );
};

const LocationFilter = props => {
  const {
    rootClassName,
    className,
    id,
    address,
    bounds,
    origin,
    onSubmit,
    contentPlacementOffset = 0,
    showAsPopup = true,
    ...rest
  } = props;

  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);
  const isSelected = !!(address || bounds);

  const defaultLabel = intl.formatMessage({ id: 'PageBuilder.SearchCTA.locationPlaceholder' });
  const selectedLabel = defaultLabel;

  const labelForPlain = (
    <span className={isSelected ? css.labelPlainSelected : css.labelPlain}>{selectedLabel}</span>
  );

  const initialValues = getInitialFormValues(address, bounds, origin);
  const ariaLabel = intl.formatMessage({ id: 'LocationAutocompleteInput.screenreader.search' });

  const handleSubmit = values => {
    const locationData = getLocationDataFromSubmit(values);

    if (locationData === null) {
      onSubmit(null);
    } else if (locationData) {
      onSubmit(locationData);
    }
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={selectedLabel}
      ariaLabel={ariaLabel}
      isSelected={isSelected}
      id={`${id}.popup`}
      showAsPopup
      labelMaxWidth={250}
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <div className={css.field}>
        <LocationField id={id} intl={intl} showAsPopup />
      </div>
    </FilterPopup>
  ) : (
    <FilterPlain
      className={classes}
      rootClassName={rootClassName}
      label={labelForPlain}
      ariaLabel={ariaLabel}
      isSelected={isSelected}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={initialValues}
      {...rest}
    >
      <div className={css.fieldPlain}>
        <LocationField id={id} intl={intl} showAsPopup={false} />
      </div>
    </FilterPlain>
  );
};

export default LocationFilter;
